import type { Ref } from '@vue/composition-api';
import { ref, computed } from '@vue/composition-api';
import { defineStore } from 'pinia';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type {
  Form,
  Cell,
  Row,
  EditorBlock,
  Node,
  CustomMergeTagPayload,
  LoadImagePayload,
  Selection,
  UpdateNodeRequest,
  Section,
  PricingSummary,
  PricingSummaryTable,
} from '@getaccept/editor-lib';
import {
  EditorHelper,
  FieldType,
  FontUpdateProgress,
  NodeType,
  RowType,
  getLoadImageURLPayloads,
} from '@getaccept/editor-lib';
import { useLoadedImagesStore } from '@getaccept/editor-lib/src/store/loaded-images.store';
import { nanoid } from 'nanoid';
import { ImageHeight } from '@getaccept/lib-shared/src/enums/image-height';
import { EditorHelper as EditorHelperLocal } from '../helpers/editor.helper';
import { SPLIT_ROW_SECOND_NODE_ID_PLACEHOLDER } from '../helpers/update-comments-dto.helper';
import { EditorBlockService } from '../api/services/editor-block.service';
import type {
  ContentRequest,
  CreateRowRequest,
  CreateSectionRequest,
  DeleteRowRequest,
  DeleteSectionRequest,
  MoveCellRequest,
  MoveRowRequest,
  MoveSectionRequest,
  SplitRowRequest,
  UpdateCellsRequest,
  UpdateContentRequest,
  UpdateSectionRequest,
} from '../api/services/types/requests';
import type { BaseRequestProperties } from '../types/base-request-properties';
import { ContentActionHelper } from './helpers/content-action.helper';
import { ContentRequestType } from './editor-ui.types';
import { ContentMutationHelper } from './helpers/content-mutation.helper';
import { useEditorUIStore } from './editor-ui.store';
import type {
  CreateRowAction,
  CreateRowsAction,
  CreateSectionsAction,
  SplitTextCreateRowAction,
  UpdateNodeAction,
  UpdateRowAction,
} from './types/actions';

export const useEditorContentStore = defineStore('editor-content', () => {
  const blocks: Ref<EditorBlock[]> = ref([]);
  const editorUIStore = useEditorUIStore();

  const mapRowIds = (rows: Row[]) =>
    rows.map(({ id, type, ...row }) => ({
      ...row,
      rowId: id,
      rowType: type,
      cells: mapCellIds(row.cells),
    }));

  const mapCellIds = (cells: Cell[]) =>
    cells.map(({ id, type, ...cell }) => ({
      ...cell,
      cellId: id,
      cellType: type,
      nodes: mapNodeIds(cell.nodes),
    }));

  const mapNodeIds = (nodes: Node[]) =>
    nodes.map(({ id, type, ...node }) => {
      const newNode = { ...node, nodeId: id, nodeType: type };
      return type === NodeType.Form
        ? {
            ...newNode,
            fields: (node as Form).fields.map(field => ({
              ...field,
              _tempSelectionId: nanoid(EditorHelper.NANO_ID_LENGTH),
            })),
          }
        : newNode;
    });

  const dispatchAndCommitUpdate = ({
    type,
    payload,
    mutation,
  }: {
    type: ContentRequestType;
    payload: Exclude<ContentRequest, UpdateContentRequest>;
    mutation: (blocks: EditorBlock[], payload: any) => EditorBlock[];
  }) => {
    const blocksBeforeUpdate = [ContentActionHelper.getBlockById(blocks.value, payload.contentId)];
    blocks.value = mutation(blocks.value, payload);
    updateDocumentPage({ targetId: payload.contentId });
    editorUIStore.enqueueRequest({ type, payload, blocksBeforeUpdate });
  };

  const updateCustomTagEverywhere = (payload: CustomMergeTagPayload) => {
    blocks.value = ContentMutationHelper.cascadeUpdateCustomMergeTags(blocks.value, payload);

    blocks.value.forEach(({ contentId }) => {
      updateDocumentPage({ targetId: contentId });
    });
  };

  const contentUpdateFailed = (blocksToReplace: EditorBlock[], error?: string) => {
    blocksToReplace.forEach(block => {
      blocks.value = ContentMutationHelper.replaceBlock(blocks.value, block);
      revertDocumentPage(block);
    });

    if (error === 'error validating domain' || error === 'link is blocked') {
      editorUIStore.setAlert({
        message: t('the_url_you_are_trying_to_add_is_blocked_for_security_reasons'),
        contentUpdateFailed: true,
      });
      return;
    }

    editorUIStore.setAlert({
      message: t('we-could-not-save-last-edit-please-try-again'),
      contentUpdateFailed: true,
    });
  };

  const updateCells = (payload: UpdateCellsRequest) => {
    dispatchAndCommitUpdate({
      type: ContentRequestType.UpdateCells,
      payload,
      mutation: ContentMutationHelper.updateCells,
    });
  };

  const updateTablesInPricingSummaries = ({
    basePayload,
    summaries,
    updateFunction,
  }: {
    basePayload: Omit<BaseRequestProperties, 'contentId'>;
    summaries: PricingSummary[];
    updateFunction: (summaryTables: PricingSummaryTable[]) => PricingSummaryTable[];
  }) => {
    summaries.forEach(({ id, type, ...summary }) => {
      const { blockId, ...target } = getParentIdsFromNodeId(id);
      blocks.value = ContentMutationHelper.updateNode(blocks.value, {
        ...basePayload,
        ...target,
        targetNodeId: id,
        payload: {
          ...summary,
          tables: updateFunction(summary.tables),
        },
      });
      updateDocumentPage({ targetId: target.contentId });
    });
  };

  const addTablesToPricingSummaries = (
    tables: PricingSummaryTable[],
    basePayload: Omit<BaseRequestProperties, 'contentId'>,
    summaries: PricingSummary[]
  ) => {
    updateTablesInPricingSummaries({
      basePayload,
      summaries,
      updateFunction: summaryTables => [...summaryTables, ...tables],
    });
  };

  const addTablesToAllPricingSummaries = (
    tables: PricingSummaryTable[],
    basePayload: Omit<BaseRequestProperties, 'contentId'>
  ) => {
    addTablesToPricingSummaries(tables, basePayload, allPricingSummaries.value);
  };

  const removeTablesFromAllPricingSummaries = (
    tableIds: string[],
    basePayload: Omit<BaseRequestProperties, 'contentId'>
  ) => {
    updateTablesInPricingSummaries({
      basePayload,
      summaries: allPricingSummaries.value,
      updateFunction: summaryTables =>
        summaryTables.filter(({ id: tableId }) => !tableIds.includes(tableId)),
    });
  };

  const updateTablesInAllPricingSummaries = (
    tables: PricingSummaryTable[],
    basePayload: Omit<BaseRequestProperties, 'contentId'>
  ) => {
    updateTablesInPricingSummaries({
      basePayload,
      summaries: allPricingSummaries.value,
      updateFunction: summaryTables =>
        summaryTables.map(summaryTable => {
          const updatedTable = tables.find(({ id }) => summaryTable.id === id);
          return updatedTable ? { ...summaryTable, ...updatedTable } : summaryTable;
        }),
    });
  };

  const createRow = ({ rowType, nodePayload, ...basePayload }: CreateRowAction) => {
    const createRowRequest: CreateRowRequest = {
      ...basePayload,
      payload: ContentActionHelper.getRowPayload(rowType, nodePayload),
    };

    const { nodeId } = createRowRequest.payload.cells[0].nodes[0];
    const selectionPayload: Selection = {
      contentId: basePayload.contentId,
      sectionId: basePayload.targetSectionId,
      rowId: createRowRequest.payload.rowId,
      cellId: nodePayload?.nodeType ? createRowRequest.payload.cells[0].cellId : undefined,
      nodeId: nodePayload?.nodeType ? nodeId : undefined,
    };

    editorUIStore.setSelection(selectionPayload);

    dispatchAndCommitUpdate({
      type: ContentRequestType.CreateRow,
      payload: createRowRequest,
      mutation: ContentMutationHelper.createRow,
    });

    if (nodePayload?.nodeType === NodeType.PricingTable) {
      addTablesToAllPricingSummaries([{ id: nodeId, include: !basePayload.hidden }], basePayload);
    }
  };

  const createSection = ({ rowType, nodePayload, ...basePayload }: CreateRowAction) => {
    const createSectionRequest: CreateSectionRequest = {
      ...basePayload,
      payload: {
        sectionId: nanoid(EditorHelper.NANO_ID_LENGTH),
        rows: [ContentActionHelper.getRowPayload(rowType, nodePayload)],
      },
    };

    const { nodeId } = createSectionRequest.payload.rows[0].cells[0].nodes[0];
    const selectionPayload: Selection = {
      contentId: basePayload.contentId,
      sectionId: createSectionRequest.payload.sectionId,
      rowId: createSectionRequest.payload.rows[0].rowId,
      cellId: nodePayload?.nodeType
        ? createSectionRequest.payload.rows[0].cells[0].cellId
        : undefined,
      nodeId: nodePayload?.nodeType ? nodeId : undefined,
    };

    editorUIStore.setSelection(selectionPayload);

    dispatchAndCommitUpdate({
      type: ContentRequestType.CreateSection,
      payload: createSectionRequest,
      mutation: ContentMutationHelper.createSection,
    });

    if (nodePayload.nodeType === NodeType.PricingTable) {
      addTablesToAllPricingSummaries([{ id: nodeId, include: true }], basePayload);
    }
  };

  const createSectionsFromResource = ({
    sections,
    contentId,
    documentId,
    dealroomId,
    resourceId,
    dsrTemplateId,
    dsrResourceId,
    append,
    targetSectionId,
    updateCommentsDto,
  }: CreateSectionsAction) => {
    sections.forEach((section, index) => {
      const createSectionRequest: CreateSectionRequest = {
        documentId,
        dealroomId,
        contentId,
        resourceId,
        dsrTemplateId,
        dsrResourceId,
        targetSectionId: index === 0 ? targetSectionId : sections[index - 1].id,
        append: index === 0 ? append : true,
        payload: {
          sectionId: section.id,
          rows: mapRowIds(
            EditorHelperLocal.updateRowFieldsWithCustomTags(section.rows, [
              ...allUniqueCustomTags.value,
              ...allUniqueMarkdownFields.value,
            ])
          ),
          hidden: section.hidden,
          displayName: section.displayName,
        },
        updateCommentsDto,
      };

      dispatchAndCommitUpdate({
        type: ContentRequestType.CreateSection,
        payload: createSectionRequest,
        mutation: ContentMutationHelper.createSection,
      });
    });

    getLoadImageURLPayloads({
      contentId,
      content: { sections },
    } as EditorBlock).forEach(loadImageURL);

    editorUIStore.resetSelection();

    if (allPricingSummaries.value.length) {
      const tables = sections.flatMap(({ rows, hidden }) =>
        rows.flatMap(row =>
          row.cells.flatMap(cell =>
            cell.nodes
              .filter(node => node.type === NodeType.PricingTable)
              .map(({ id }) => ({ id, include: !hidden }))
          )
        )
      );

      if (tables.length) {
        addTablesToAllPricingSummaries(tables, {
          documentId,
          dealroomId,
          resourceId,
          dsrTemplateId,
          dsrResourceId,
        });
      }
    }
  };

  const createRowsFromResource = ({
    rows,
    contentId,
    documentId,
    dealroomId,
    resourceId,
    dsrTemplateId,
    dsrResourceId,
    append,
    targetSectionId,
    targetRowId,
    updateCommentsDto,
    hidden,
  }: CreateRowsAction) => {
    EditorHelperLocal.updateRowFieldsWithCustomTags(rows, [
      ...allUniqueCustomTags.value,
      ...allUniqueMarkdownFields.value,
    ]).forEach(({ id, type, ...row }, index) => {
      const createRowRequest: CreateRowRequest = {
        documentId,
        dealroomId,
        contentId,
        resourceId,
        dsrTemplateId,
        dsrResourceId,
        targetSectionId,
        targetRowId: index === 0 ? targetRowId : rows[index - 1].id,
        append: index === 0 ? append : true,
        payload: {
          ...row,
          rowId: id,
          rowType: type,
          cells: mapCellIds(row.cells),
        },
        updateCommentsDto,
        hidden,
      };

      dispatchAndCommitUpdate({
        type: ContentRequestType.CreateRow,
        payload: createRowRequest,
        mutation: ContentMutationHelper.createRow,
      });
    });

    getLoadImageURLPayloads({
      contentId,
      content: { sections: [{ id: targetSectionId, rows }] },
    } as EditorBlock).forEach(loadImageURL);

    editorUIStore.resetSelection();

    if (allPricingSummaries.value.length) {
      const tables = rows
        .flatMap(row => row.cells)
        .flatMap(cell => cell.nodes)
        .filter(node => node.type === NodeType.PricingTable)
        .map(({ id }) => ({ id, include: !hidden }));

      if (tables.length) {
        addTablesToAllPricingSummaries(tables, {
          documentId,
          dealroomId,
          resourceId,
          dsrTemplateId,
          dsrResourceId,
        });
      }
    }
  };

  const splitTextCreateRowWithResources = (actionPayload: SplitTextCreateRowAction) => {
    const {
      documentId,
      dealroomId,
      resourceId,
      dsrTemplateId,
      dsrResourceId,
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      firstNodeContent,
      firstNodeFields,
      lastNodeContent,
      lastNodeFields,
      nodePayload,
      hidden,
    } = actionPayload;

    const rows = EditorHelperLocal.updateRowFieldsWithCustomTags(actionPayload.rows, [
      ...allUniqueCustomTags.value,
      ...allUniqueMarkdownFields.value,
    ]);

    const updateFirstNodeRequest = {
      documentId,
      dealroomId,
      resourceId,
      dsrTemplateId,
      dsrResourceId,
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      payload: { content: firstNodeContent, fields: firstNodeFields },
    } as UpdateNodeRequest;

    const createResourceRowsRequest = rows.map(({ id, type, ...row }, index) => ({
      documentId,
      dealroomId,
      contentId,
      resourceId,
      dsrTemplateId,
      dsrResourceId,
      targetSectionId,
      targetRowId: index === 0 ? targetRowId : rows[index - 1].id,
      append: true,
      payload: {
        ...row,
        rowId: id,
        rowType: type,
        cells: mapCellIds(row.cells),
      },
      hidden,
    }));

    const lastResourceRow = createResourceRowsRequest[createResourceRowsRequest.length - 1];

    const createLastRowRequest = {
      documentId,
      dealroomId,
      resourceId,
      dsrTemplateId,
      dsrResourceId,
      contentId,
      targetSectionId,
      targetRowId: lastResourceRow.payload.rowId,
      append: true,
      payload: ContentActionHelper.getRowPayload(RowType.Standard, {
        nodeType: NodeType.Text,
        content: lastNodeContent,
        fields: lastNodeFields,
      }),
      hidden,
    } as CreateRowRequest;

    const selectionPayload: Selection = {
      contentId,
      sectionId: targetSectionId,
      rowId: lastResourceRow.payload.rowId,
      cellId: nodePayload?.nodeType ? lastResourceRow.payload.cells[0].cellId : undefined,
      nodeId: nodePayload?.nodeType ? lastResourceRow.payload.cells[0].nodes[0].nodeId : undefined,
    };

    const splitRowRequest: SplitRowRequest = {
      documentId,
      dealroomId,
      contentId,
      resourceId,
      dsrTemplateId,
      dsrResourceId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      splitInput: updateFirstNodeRequest.payload,
      createInput: [
        ...createResourceRowsRequest.map(resource => resource.payload),
        createLastRowRequest.payload,
      ],
      hidden,
    };

    updateDocumentPage({ targetId: contentId });
    editorUIStore.enqueueRequest({
      type: ContentRequestType.SplitTextCreateRow,
      payload: splitRowRequest,
      blocksBeforeUpdate: [ContentActionHelper.getBlockById(blocks.value, contentId)],
    });

    blocks.value = ContentMutationHelper.updateNode(blocks.value, updateFirstNodeRequest);
    createResourceRowsRequest.forEach(request => {
      blocks.value = ContentMutationHelper.createRow(blocks.value, request);
    });
    blocks.value = ContentMutationHelper.createRow(blocks.value, createLastRowRequest);

    getLoadImageURLPayloads({
      contentId,
      content: { sections: [{ id: targetSectionId, rows }] },
    } as EditorBlock).forEach(loadImageURL);

    editorUIStore.setSelection(selectionPayload);

    if (allPricingSummaries.value.length) {
      const tables = rows
        .flatMap(row => row.cells)
        .flatMap(cell => cell.nodes)
        .filter(node => node.type === NodeType.PricingTable)
        .map(({ id }) => ({ id, include: !hidden }));

      if (tables.length) {
        addTablesToAllPricingSummaries(tables, {
          documentId,
          dealroomId,
          resourceId,
          dsrTemplateId,
          dsrResourceId,
        });
      }
    }
  };

  const splitTextCreateRow = (actionPayload: SplitTextCreateRowAction) => {
    const {
      documentId,
      dealroomId,
      resourceId,
      dsrTemplateId,
      dsrResourceId,
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      firstNodeContent,
      firstNodeFields,
      lastNodeContent,
      lastNodeFields,
      nodePayload,
      rowType,
      updateCommentsDto,
      hidden,
    } = actionPayload;

    const updateFirstNodeRequest = {
      documentId,
      dealroomId,
      resourceId,
      dsrTemplateId,
      dsrResourceId,
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      payload: { content: firstNodeContent, fields: firstNodeFields },
    };

    const createRowRequest = {
      documentId,
      dealroomId,
      resourceId,
      dsrTemplateId,
      dsrResourceId,
      contentId,
      targetSectionId,
      targetRowId,
      append: true,
      hidden,
      payload: ContentActionHelper.getRowPayload(rowType, nodePayload),
    };

    const createLastRowRequest = {
      documentId,
      dealroomId,
      resourceId,
      dsrTemplateId,
      dsrResourceId,
      contentId,
      targetSectionId,
      targetRowId: createRowRequest.payload.rowId,
      append: true,
      hidden,
      payload: ContentActionHelper.getRowPayload(RowType.Standard, {
        nodeType: NodeType.Text,
        content: lastNodeContent,
        fields: lastNodeFields,
      }),
    };

    const selectionPayload: Selection = {
      contentId,
      sectionId: targetSectionId,
      rowId: createRowRequest.payload.rowId,
      cellId: nodePayload?.nodeType ? createRowRequest.payload.cells[0].cellId : undefined,
      nodeId: nodePayload?.nodeType ? createRowRequest.payload.cells[0].nodes[0].nodeId : undefined,
    };

    const splitRowRequest: SplitRowRequest = {
      documentId,
      dealroomId,
      contentId,
      resourceId,
      dsrTemplateId,
      dsrResourceId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      splitInput: updateFirstNodeRequest.payload,
      createInput: [createRowRequest.payload, createLastRowRequest.payload],
      hidden,
      updateCommentsDto: updateCommentsDto
        ? {
            comments: updateCommentsDto.comments.map(comment =>
              comment.editorSelection?.nodeId === SPLIT_ROW_SECOND_NODE_ID_PLACEHOLDER
                ? {
                    ...comment,
                    editorSelection: {
                      ...comment.editorSelection,
                      rowId: createLastRowRequest.payload.rowId,
                      cellId: createLastRowRequest.payload.cells[0].cellId,
                      nodeId: createLastRowRequest.payload.cells[0].nodes[0].nodeId,
                    },
                  }
                : comment
            ),
          }
        : undefined,
    };

    updateDocumentPage({ targetId: contentId });
    editorUIStore.enqueueRequest({
      type: ContentRequestType.SplitTextCreateRow,
      payload: splitRowRequest,
      blocksBeforeUpdate: [ContentActionHelper.getBlockById(blocks.value, contentId)],
    });

    blocks.value = ContentMutationHelper.updateNode(blocks.value, updateFirstNodeRequest);
    blocks.value = ContentMutationHelper.createRow(blocks.value, createRowRequest);
    blocks.value = ContentMutationHelper.createRow(blocks.value, createLastRowRequest);

    editorUIStore.setSelection(selectionPayload);

    if (allPricingSummaries.value.length) {
      const tables = createRowRequest.payload.cells
        .flatMap(cell => cell.nodes)
        .filter(node => node.nodeType === NodeType.PricingTable)
        .map(({ nodeId: id }) => ({ id, include: !hidden }));

      if (tables.length) {
        addTablesToAllPricingSummaries(tables, {
          documentId,
          dealroomId,
          resourceId,
          dsrTemplateId,
          dsrResourceId,
        });
      }
    }
  };

  const deleteRow = (payload: DeleteRowRequest) => {
    let tableIds: string[] = [];
    if (allPricingSummaries.value.length) {
      tableIds = blocks.value
        .find(block => block.contentId === payload.contentId)
        ?.content.sections.find(section => section.id === payload.targetSectionId)
        ?.rows.find(row => row.id === payload.targetRowId)
        ?.cells.flatMap(cell => cell.nodes)
        .filter(node => node.type === NodeType.PricingTable)
        .map(({ id }) => id);
    }

    dispatchAndCommitUpdate({
      type: ContentRequestType.DeleteRow,
      payload,
      mutation: ContentMutationHelper.deleteRow,
    });

    if (tableIds.length) {
      removeTablesFromAllPricingSummaries(tableIds, {
        documentId: payload.documentId,
        dealroomId: payload.dealroomId,
        resourceId: payload.resourceId,
        dsrTemplateId: payload.dsrTemplateId,
        dsrResourceId: payload.dsrResourceId,
      });
    }
  };

  const deleteSection = (payload: DeleteSectionRequest) => {
    let deletedPricingTableIds: string[] = [];
    if (allPricingSummaries.value.length) {
      deletedPricingTableIds = blocks.value
        .find(block => block.contentId === payload.contentId)
        ?.content.sections.find(section => section.id === payload.targetSectionId)
        ?.rows.flatMap(row => row.cells)
        .flatMap(cell => cell.nodes)
        .filter(node => node.type === NodeType.PricingTable)
        .map(({ id }) => id);
    }

    dispatchAndCommitUpdate({
      type: ContentRequestType.DeleteSection,
      payload: { ...payload, deletedPricingTableIds },
      mutation: ContentMutationHelper.deleteSection,
    });

    if (deletedPricingTableIds.length) {
      removeTablesFromAllPricingSummaries(deletedPricingTableIds, {
        documentId: payload.documentId,
        dealroomId: payload.dealroomId,
        resourceId: payload.resourceId,
        dsrTemplateId: payload.dsrTemplateId,
        dsrResourceId: payload.dsrResourceId,
      });
    }
  };

  const updateRow = ({
    documentId,
    dealroomId,
    resourceId,
    dsrTemplateId,
    dsrResourceId,
    contentId,
    targetSectionId,
    targetRowId,
    cellCount,
    locked,
    columnCount,
    targetNodeId,
    nodePayload,
    updateCommentsDto,
  }: UpdateRowAction) => {
    const row = blocks.value
      .find(block => block.contentId === contentId)
      ?.content.sections.find(section => section.id === targetSectionId)
      ?.rows.find(row => row.id === targetRowId);

    if (row.type === RowType.SlideShow) {
      delete row._tempSlideShowId;
      delete row._tempSlideShowName;
    }

    const cells = ContentActionHelper.getUpdatedCells(
      cellCount,
      row,
      locked,
      nodePayload,
      targetNodeId
    );

    const actionPayload = {
      documentId,
      dealroomId,
      resourceId,
      dsrTemplateId,
      dsrResourceId,
      contentId,
      targetSectionId,
      targetRowId,
      payload: {
        cells,
        columnCount,
      },
      updateCommentsDto,
    };

    dispatchAndCommitUpdate({
      type: ContentRequestType.UpdateRow,
      payload: actionPayload,
      mutation: ContentMutationHelper.updateRow,
    });
  };

  const updateSection = (actionPayload: UpdateSectionRequest) => {
    dispatchAndCommitUpdate({
      type: ContentRequestType.UpdateSection,
      payload: actionPayload,
      mutation: ContentMutationHelper.updateSection,
    });

    if ('hidden' in actionPayload.payload && allPricingSummaries.value.length) {
      const tableIds = blocks.value
        .find(block => block.contentId === actionPayload.contentId)
        ?.content.sections.find(section => section.id === actionPayload.targetSectionId)
        ?.rows.flatMap(row => row.cells)
        .flatMap(cell => cell.nodes)
        .filter(node => node.type === NodeType.PricingTable)
        .map(({ id }) => id);

      if (!tableIds.length) {
        return;
      }

      const baseRequestProperties = {
        documentId: actionPayload.documentId,
        dealroomId: actionPayload.dealroomId,
        resourceId: actionPayload.resourceId,
        dsrTemplateId: actionPayload.dsrTemplateId,
        dsrResourceId: actionPayload.dsrResourceId,
      };

      updateTablesInAllPricingSummaries(
        tableIds.map(id => ({ id, include: !actionPayload.payload.hidden })),
        baseRequestProperties
      );
    }
  };

  const moveRow = (payload: MoveRowRequest) => {
    dispatchAndCommitUpdate({
      type: ContentRequestType.MoveRow,
      payload,
      mutation: ContentMutationHelper.moveRow,
    });
  };

  const moveSection = (payload: MoveSectionRequest) => {
    updateDocumentPage({ targetId: payload.contentId });
    if (payload.targetContentId) {
      updateDocumentPage({ targetId: payload.targetContentId });
    }

    editorUIStore.resetSelection();

    editorUIStore.enqueueRequest({
      type: ContentRequestType.MoveSection,
      payload,
      blocksBeforeUpdate: [
        ContentActionHelper.getBlockById(blocks.value, payload.contentId),
        ...(payload.targetContentId
          ? [ContentActionHelper.getBlockById(blocks.value, payload.targetContentId)]
          : []),
      ],
    });

    blocks.value = ContentMutationHelper.moveSection(blocks.value, payload);
  };

  const moveCell = (payload: MoveCellRequest) => {
    dispatchAndCommitUpdate({
      type: ContentRequestType.MoveCell,
      payload,
      mutation: ContentMutationHelper.moveCell,
    });
  };

  const updateNode = (payload: UpdateNodeAction) => {
    dispatchAndCommitUpdate({
      type: ContentRequestType.UpdateNode,
      payload,
      mutation: ContentMutationHelper.updateNode,
    });
  };

  const updateDocumentPage = ({
    targetId,
    noUpdate = false,
  }: {
    targetId: string;
    noUpdate?: boolean;
  }) => {
    editorUIStore.setBlockToUpdate(
      ContentActionHelper.getBlockById(blocks.value, targetId),
      noUpdate
    );
  };

  const revertDocumentPage = (block: EditorBlock) => {
    editorUIStore.setBlockToUpdate(block);
  };

  const setBlock = (block: EditorBlock) => {
    blocks.value = ContentMutationHelper.setBlock(blocks.value, block);
    setBlockImageURLs(block);
  };

  const setUpdatedBlock = (block: EditorBlock) => {
    blocks.value = ContentMutationHelper.setBlock(blocks.value, block);
  };

  const setBlockImageURLs = (block: EditorBlock) => {
    getLoadImageURLPayloads(block).forEach(loadImageURL);
  };

  const loadImageURL = async ({ imageId, ...loadImagePayload }: LoadImagePayload) => {
    try {
      const { url: srcUrl, externalId, blurhash } = await EditorBlockService.getImageUrl(
        imageId,
        ImageHeight.QHD
      );
      useLoadedImagesStore().setImage(imageId, srcUrl, externalId, blurhash);
      blocks.value = ContentMutationHelper.updateNode(blocks.value, {
        ...loadImagePayload,
        payload: { srcUrl, externalId, blurhash },
      });
      updateDocumentPage({ targetId: loadImagePayload.contentId, noUpdate: true });
    } catch (err) {
      console.error(err);
    }
  };

  const updateContentFontSettings = (payload: UpdateContentRequest) => {
    editorUIStore.setFontUpdateProgress(FontUpdateProgress.InProgress);
    editorUIStore.enqueueRequest({
      type: ContentRequestType.UpdateContentFontSettings,
      payload,
      blocksBeforeUpdate: [
        ContentActionHelper.getBlockById(blocks.value, payload.targetContentIds[0]),
      ],
    });

    blocks.value = ContentMutationHelper.updateContent(blocks.value, payload);
  };

  const removeBlock = (id: string) => {
    blocks.value = ContentMutationHelper.removeBlock(blocks.value, id);
  };

  const resetBlocks = () => {
    blocks.value = [];
  };

  const updateContent = (payload: UpdateContentRequest) => {
    updateDocumentPage({ targetId: payload.targetContentIds[0] });
    editorUIStore.enqueueRequest({
      type: ContentRequestType.UpdateContent,
      payload,
      blocksBeforeUpdate: [
        ContentActionHelper.getBlockById(blocks.value, payload.targetContentIds[0]),
      ],
    });

    blocks.value = ContentMutationHelper.updateContent(blocks.value, payload);
  };

  const setEditorBlockVersions = (payload: Record<string, string>) => {
    blocks.value = ContentMutationHelper.setEditorBlockVersions(blocks.value, payload);
  };

  const allEditorFields = computed(() => EditorHelper.getFields(blocks.value));

  const allUniqueCustomTags = computed(() => {
    const allCustomMergeTags = EditorHelper.getFields(blocks.value).filter(
      ({ type }) => type === FieldType.Custom
    );
    const allCustomNames: string[] = allCustomMergeTags.map(({ customName }) => customName);
    const uniqueCustomNames = Array.from(new Set(allCustomNames));

    return uniqueCustomNames.map(name =>
      allCustomMergeTags.find(({ customName }) => customName === name)
    );
  });

  const allUniqueMarkdownFields = computed(() => {
    const allCustomMarkdownFields = EditorHelper.getFields(blocks.value).filter(
      ({ type }) => type === FieldType.Markdown
    );
    const allCustomNames = allCustomMarkdownFields.map(({ customName }) => customName);
    const uniqueCustomNames = Array.from(new Set(allCustomNames));

    return uniqueCustomNames.map(name =>
      allCustomMarkdownFields.find(({ customName }) => customName === name)
    );
  });

  const allPricingTables = computed(() => EditorHelper.getAllPricingTablesFromBlocks(blocks.value));

  const allPricingSummaries = computed(() =>
    EditorHelper.getAllPricingSummariesFromBlocks(blocks.value)
  );

  const getParentIdsFromNodeId = (nodeId: string) => {
    let foundCell: Cell, foundRow: Row, foundSection: Section, foundBlock: EditorBlock;

    blocks.value.some(block =>
      block.content.sections.some(section =>
        section.rows.some(row =>
          row.cells.some(cell => {
            if (cell.nodes.some(node => node.id === nodeId)) {
              foundCell = cell;
              foundRow = row;
              foundSection = section;
              foundBlock = block;
              return true;
            }
            return false;
          })
        )
      )
    );

    return {
      blockId: foundBlock.id,
      contentId: foundBlock.contentId,
      targetSectionId: foundSection.id,
      targetRowId: foundRow.id,
      targetCellId: foundCell.id,
    };
  };

  return {
    blocks,
    allEditorFields,
    allUniqueCustomTags,
    allUniqueMarkdownFields,
    allPricingSummaries,
    allPricingTables,
    updateTablesInPricingSummaries,
    removeTablesFromAllPricingSummaries,
    addTablesToAllPricingSummaries,
    addTablesToPricingSummaries,
    updateTablesInAllPricingSummaries,

    getParentIdsFromNodeId,
    updateCustomTagEverywhere,
    contentUpdateFailed,
    updateCells,
    createRow,
    createSection,
    createSectionsFromResource,
    createRowsFromResource,
    splitTextCreateRowWithResources,
    splitTextCreateRow,
    deleteRow,
    deleteSection,
    updateRow,
    updateSection,
    moveRow,
    moveSection,
    moveCell,
    updateNode,
    updateDocumentPage,
    revertDocumentPage,
    setBlock,
    setUpdatedBlock,
    setBlockImageURLs,
    loadImageURL,
    updateContentFontSettings,
    removeBlock,
    resetBlocks,
    updateContent,
    setEditorBlockVersions,
  };
});
